import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/hand-sanitizer-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import sanitizerDisposal from '../../json/hand-sanitizer-disposal-data.json'

const sanitizerServiceData = {
  name: 'Hand Sanitizer Disposal',
  description:
    'Bulk hand sanitizer disposal and recycling for businesses, government, and institutions. Dimension safely removes expired, surplus, and recalled sanitizer with EPA/RCRA-compliant documentation.',
  serviceType: 'Hand Sanitizer Disposal',
  url: 'https://getdimension.com/hand-sanitizer-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Expired Hand Sanitizer Disposal' },
    { name: 'Surplus Hand Sanitizer Removal' },
    { name: 'Recalled Hand Sanitizer Disposal' },
    { name: 'Bulk Pallet Sanitizer Pickup' },
    { name: 'Ethanol Reclamation and Recycling' },
  ],
}

const sanitizerFAQData = {
  questions: sanitizerDisposal.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function HandSanitizerDisposal() {
  return (
    <>
      <Head>
        <title>Hand Sanitizer Disposal for Businesses | Dimension</title>
        <meta
          name="description"
          content="Bulk hand sanitizer disposal and recycling for businesses and government. Dimension removes expired, surplus & recalled sanitizer, EPA/RCRA-compliant, from around $50 per pallet. Get an instant quote."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/hand-sanitizer-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={sanitizerServiceData} />
        <SchemaMarkup type="FAQPage" data={sanitizerFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="hand-sanitizer"
          heading={sanitizerDisposal.pitch.heading}
          material={sanitizerDisposal.pitch.material}
          pitchText={sanitizerDisposal.pitch.pitchText}
          ctaLink={sanitizerDisposal.pitch.ctaLink}
          ctaText={sanitizerDisposal.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...sanitizerDisposal.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...sanitizerDisposal.benefits} />
        <CaseStudy {...sanitizerDisposal.caseStudy} />
        <Approach {...sanitizerDisposal.approach} />
        <FAQ {...sanitizerDisposal.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hand Sanitizer Recycling',
              href: '/hand-sanitizer-recycling',
              description:
                'Recycle expired hand sanitizer through ethanol reclamation and donation of usable surplus.',
            },
            {
              label: 'PPE Disposal and Recycling',
              href: '/ppe-disposal',
              description:
                'Compliant disposal of masks, gloves, gowns, and other expired or surplus PPE for businesses.',
            },
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'Compliant disposal of solvents, used fuel, aerosols, and other DOT Class 3 ignitable waste.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default HandSanitizerDisposal
